import { canonicalSegmentPrimaryLabel, SEGMENT_PRIMARY_VALUES } from "./constants";

export const CAMPAIGN_STEPS = [
  { key: "ironfleet_at_booth", label: "Ironfleet will be at booth" },
  { key: "panel_presentation", label: "See the Panel Presentation" },
  { key: "thanks_for_visiting", label: "Thanks for visiting our booth" },
  { key: "show_followup", label: "Show Follow-up" },
] as const;

export type CampaignActivityKey = (typeof CAMPAIGN_STEPS)[number]["key"];

export interface CampaignJourneyData {
  campaign_key: string;
  activities: Record<string, Record<string, unknown> | null>;
  filled: number;
  total: number;
}

export interface RatingBoardRow {
  account_id: string;
  name: string | null;
  domain: string | null;
  booth: string | null;
  hall: string | null;
  contact_count: number;
  segment: string | null;
  account_types?: string[];
  segments?: string[];
  ai_rating: number | null;
  notes: { note_id: string; body: string | null; created_at: string; actor: string } | null;
  my_favorite?: boolean;
  my_view?: "high" | "med" | null;
  my_owner?: boolean;
  other_ratings: string;
  owners: string[];
  campaign: CampaignJourneyData;
  classification?: Record<string, unknown> | null;
}

export interface RatingBoardResult {
  results: RatingBoardRow[];
  campaign_key?: string;
  has_more?: boolean;
  next_cursor?: string | null;
}

/** Walk plan is for show-floor exhibitors (`intel.account_types`), not CRM-only accounts. */
export function accountSupportsWalkPlan(accountTypes?: string[] | null): boolean {
  if (!accountTypes?.length) return false;
  return accountTypes.some((raw) => {
    const type = raw.trim().toLowerCase().replace(/\s+/g, "_");
    return type === "exhibitor";
  });
}

/** Thin `account_search` row, plus optional board fields if a later MCP adds them. */
export interface AccountSearchHit {
  account_id: string;
  name?: string | null;
  domain?: string | null;
  exhibitor_id?: string | null;
  booth?: string | null;
  hall?: string | null;
  booths?: unknown;
  booth_numbers?: unknown;
  halls?: unknown;
  exhibiting?: {
    booth_number?: string | null;
    hall?: string | null;
    booths?: unknown;
    booth_numbers?: unknown;
    halls?: unknown;
  } | null;
  contact_count?: number;
  segment?: string | null;
  account_types?: string[];
  segments?: string[];
  ai_rating?: number | null;
  notes?: RatingBoardRow["notes"];
  my_favorite?: boolean;
  my_view?: "high" | "med" | null;
  my_owner?: boolean;
  attribution?: string[] | null;
  other_ratings?: string;
  owners?: string[];
  campaign?: CampaignJourneyData;
  classification?: Record<string, unknown> | null;
  profile?: Record<string, unknown> | null;
  raw_attributes?: Record<string, unknown> | null;
  extensions?: {
    exhibitor?: { booth_number?: string | null; hall?: string | null } | null;
  } | null;
}

export interface AccountSearchResult {
  results: AccountSearchHit[];
  total?: number;
  has_more?: boolean;
  next_cursor?: string | null;
}

export function emptyCampaign(campaignKey = ""): CampaignJourneyData {
  return {
    campaign_key: campaignKey,
    activities: {},
    filled: 0,
    total: CAMPAIGN_STEPS.length,
  };
}

function asTrimmedText(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function nestedRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function firstBoothLike(value: unknown): { booth: string | null; hall: string | null } {
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstBoothLike(item);
      if (found.booth || found.hall) return found;
    }
    return { booth: null, hall: null };
  }
  const text = asTrimmedText(value);
  if (text) return { booth: text, hall: null };
  const rec = nestedRecord(value);
  if (!rec) return { booth: null, hall: null };
  return {
    booth: asTrimmedText(rec.booth_number) || asTrimmedText(rec.booth) || asTrimmedText(rec.booth_id),
    hall: asTrimmedText(rec.hall) || asTrimmedText(rec.hall_name) || asTrimmedText(rec.hall_id),
  };
}

function firstHall(value: unknown): string | null {
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstHall(item);
      if (found) return found;
    }
    return null;
  }
  const text = asTrimmedText(value);
  if (text) return text;
  const rec = nestedRecord(value);
  return rec ? asTrimmedText(rec.hall) || asTrimmedText(rec.hall_name) || asTrimmedText(rec.hall_id) : null;
}

function isSegmentPrimaryValue(value: string): boolean {
  return (SEGMENT_PRIMARY_VALUES as readonly string[]).includes(canonicalSegmentPrimaryLabel(value));
}

function segmentPrimaryFromAccountSearch(hit: AccountSearchHit): string | null {
  const classification = nestedRecord(hit.classification);
  const fromClassification = asTrimmedText(classification?.segment_primary);
  if (fromClassification) return canonicalSegmentPrimaryLabel(fromClassification);
  const fromSegment = asTrimmedText(hit.segment);
  return fromSegment && isSegmentPrimaryValue(fromSegment) ? canonicalSegmentPrimaryLabel(fromSegment) : null;
}

function segmentLabelsFromAccountSearch(hit: AccountSearchHit): string[] {
  const primary = segmentPrimaryFromAccountSearch(hit);
  return primary ? [primary] : [];
}

export function boothIdsFromAccountSearch(hit: AccountSearchHit): string[] {
  const ids = new Set<string>();
  const add = (value: unknown) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      ids.add(String(value).toLowerCase());
      return;
    }
    if (typeof value === "string" && value.trim()) ids.add(value.trim().toLowerCase());
  };
  const addBoothFields = (value: unknown, depth = 0) => {
    if (depth > 5 || value == null) return;
    if (typeof value === "string" || typeof value === "number") {
      add(value);
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) addBoothFields(item, depth + 1);
      return;
    }
    const rec = nestedRecord(value);
    if (!rec) return;
    add(rec.booth_number);
    add(rec.booth);
    add(rec.booth_id);
    addBoothFields(rec.booths, depth + 1);
    addBoothFields(rec.booth_numbers, depth + 1);
  };
  add(hit.booth);
  addBoothFields(hit.booths);
  addBoothFields(hit.booth_numbers);
  addBoothFields(hit.exhibiting);
  addBoothFields(hit.extensions?.exhibitor);
  add(nestedRecord(hit.profile)?.booth_number);
  add(nestedRecord(hit.raw_attributes)?.booth_number);
  add(boothFromAccountSearch(hit).booth);
  return [...ids];
}

function boothFromAccountSearch(hit: AccountSearchHit): { booth: string | null; hall: string | null } {
  const profile = nestedRecord(hit.profile);
  const raw = nestedRecord(hit.raw_attributes);
  const exhibitor = nestedRecord(hit.extensions?.exhibitor);
  const exhibiting = nestedRecord(hit.exhibiting);
  const fromBooths = firstBoothLike(hit.booths);
  const fromNumbers = firstBoothLike(hit.booth_numbers);
  const fromExhibiting = firstBoothLike(exhibiting?.booths);
  const fromExhibitingNumbers = firstBoothLike(exhibiting?.booth_numbers);
  return {
    booth:
      asTrimmedText(hit.booth) ||
      fromBooths.booth ||
      fromNumbers.booth ||
      asTrimmedText(exhibiting?.booth_number) ||
      fromExhibiting.booth ||
      fromExhibitingNumbers.booth ||
      asTrimmedText(exhibitor?.booth_number) ||
      asTrimmedText(raw?.booth_number) ||
      asTrimmedText(profile?.booth_number),
    hall:
      asTrimmedText(hit.hall) ||
      fromBooths.hall ||
      firstHall(hit.halls) ||
      asTrimmedText(exhibiting?.hall) ||
      fromExhibiting.hall ||
      firstHall(exhibiting?.halls) ||
      asTrimmedText(exhibitor?.hall) ||
      asTrimmedText(raw?.hall) ||
      asTrimmedText(profile?.hall),
  };
}

/** Map `account_search` into the Account Viewer row shape. Missing campaign fields stay empty. */
export function accountSearchHitToBoardRow(hit: AccountSearchHit, campaignKey = ""): RatingBoardRow {
  const { booth, hall } = boothFromAccountSearch(hit);
  return {
    account_id: hit.account_id,
    name: hit.name ?? null,
    domain: hit.domain ?? null,
    booth,
    hall,
    contact_count: Number(hit.contact_count ?? 0),
    segment: segmentPrimaryFromAccountSearch(hit),
    account_types: hit.account_types,
    segments: segmentLabelsFromAccountSearch(hit),
    ai_rating: hit.ai_rating ?? null,
    notes: hit.notes ?? null,
    my_favorite: hit.my_favorite,
    my_view: hit.my_view,
    my_owner: hit.my_owner,
    other_ratings: hit.other_ratings ?? "",
    owners: hit.owners ?? [],
    campaign: hit.campaign ?? emptyCampaign(campaignKey),
    classification: nestedRecord(hit.classification),
  };
}

export function ratingLineage(): string {
  const day = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  return `CT-WEB-RATING-${day}`;
}

export function isSameUserKey(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Local-part first token from an email (or raw user key), proper-cased. */
export function firstNameFromUserKey(userKey: string): string {
  const local = userKey.trim().split("@")[0] ?? "";
  const token = local.split(/[._+]/)[0] ?? "";
  if (!token) return userKey.trim();
  return token
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("-");
}

export function formatOtherRatings(raw: string, viewer: string): string {
  if (!raw.trim()) return "";
  return raw
    .split(/\s·\s/)
    .map((part) => {
      const idx = part.lastIndexOf(":");
      const user = (idx >= 0 ? part.slice(0, idx) : part).trim();
      const rating = idx >= 0 ? part.slice(idx + 1).trim() : "";
      if (!user || isSameUserKey(user, viewer)) return null;
      const name = firstNameFromUserKey(user);
      return rating ? `${name}: ${rating}` : name;
    })
    .filter((value): value is string => Boolean(value))
    .join(" · ");
}

export function formatOwnerNames(owners: string[], viewer: string): string {
  return owners.filter((owner) => !isSameUserKey(owner, viewer)).map(firstNameFromUserKey).join(", ");
}

const NOTE_VIEW_MIN = 300;
const NOTE_VIEW_MAX = 320;

/** Display-only preview. Stored note bodies are not mutated. */
/** Local datetime stamp for note / follow-up history. */
export function formatNoteStamp(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function formatNotePreview(body: string | null | undefined): string {
  if (!body) return "—";
  if (body.length <= NOTE_VIEW_MAX) return body;
  let cut = NOTE_VIEW_MAX;
  for (let i = NOTE_VIEW_MAX - 1; i >= NOTE_VIEW_MIN; i--) {
    if (/\s/.test(body[i]!)) {
      cut = i;
      break;
    }
  }
  return `${body.slice(0, cut).trimEnd()}...`;
}
